import { fetchEntityList, fetchEntityExport, writeEntity, getJson } from '@/lib/entity-api';
import { getApiUrl } from '@/lib/api-config';
import { fetchCostCodeList, type CostCode } from '@/lib/cost-codes';

/**
 * The Catalog's data layer: cost codes, items and the vendor offers of each item, all through the generic entity API
 * (`cost_code`, `catalog_item`, `catalog_item_offer`). Nothing here talks to Supabase.
 */
export type { CostCode };

export interface CatalogOffer {
  id: string;
  item_id: string;
  vendor_id: string;
  vendor?: { id: string; company_name?: string | null; first_name?: string | null; last_name?: string | null } | null;
  unit_price: number;
  vendor_sku: string | null;
  purchase_unit: string | null;
  use_unit: string | null;
  conversion_factor: number | null;
  lead_time_days: number | null;
  price_updated_at: string;
  is_active: boolean;
  is_preferred: boolean;
}

export interface CatalogItem {
  id: string;
  name: string;
  description: string | null;
  sku: string | null;
  cost_code_id: string | null;
  cost_code?: { id: string; code: string; name: string | null } | null;
  unit: string | null;
  is_active: boolean;
  created_at: string;
  /** the item's offers (all of them), read for the rows on screen */
  offers?: CatalogOffer[];
}

export const vendorName = (vendor?: CatalogOffer['vendor']): string =>
  (vendor && (vendor.company_name || `${vendor.first_name || ''} ${vendor.last_name || ''}`.trim())) || 'Unknown vendor';

/** The server accepts up to this many values in one filter (core/entity-query.js MAX_FILTER_VALUES). */
const MAX_FILTER_IDS = 50;

/**
 * The offers of these items, grouped by item (one request per 50 items -- a page of the table is usually one). Numbers arrive as numbers:
 * numeric columns are normalised here. Each id travels as its own value of the `item_id` filter; the server validates every one as a uuid.
 */
export async function fetchOffersByItem(itemIds: string[]): Promise<Map<string, CatalogOffer[]>> {
  const grouped = new Map<string, CatalogOffer[]>();
  const unique = Array.from(new Set(itemIds));
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += MAX_FILTER_IDS) chunks.push(unique.slice(i, i + MAX_FILTER_IDS));
  const pages = await Promise.all(chunks.map((chunk) => fetchEntityList<any>('catalog_item_offer', { filters: { item_id: chunk } })));
  for (const offer of pages.flat()) {
    const normalised: CatalogOffer = { ...offer, unit_price: Number(offer.unit_price), conversion_factor: offer.conversion_factor === null ? null : Number(offer.conversion_factor) };
    grouped.set(offer.item_id, [...(grouped.get(offer.item_id) ?? []), normalised]);
  }
  return grouped;
}

/** The company's cost codes (all of them; selects show the active ones). */
export const fetchCostCodes = fetchCostCodeList;

/** Every item of the company (duplicate detection for the import). */
export const fetchAllItems = () => fetchEntityExport<CatalogItem>('catalog_item', {});

export const saveCostCode = (id: string | null, body: Record<string, unknown>) =>
  id ? writeEntity<CostCode>(`api/entities/cost_code/${id}`, 'PUT', body, 'Failed to save the cost code') : writeEntity<CostCode>('api/entities/cost_code', 'POST', body, 'Failed to save the cost code');

export const saveItem = (id: string | null, body: Record<string, unknown>) =>
  id ? writeEntity<CatalogItem>(`api/entities/catalog_item/${id}`, 'PUT', body, 'Failed to save the item') : writeEntity<CatalogItem>('api/entities/catalog_item', 'POST', body, 'Failed to save the item');

/**
 * An item AND its vendor offers, saved in ONE request / one database transaction (all or nothing). `offers` are upserts: one with `id`
 * updates that offer, one without creates it; offers not listed are left as they are.
 */
export const saveItemWithOffers = (id: string | null, body: { item: Record<string, unknown>; offers: Array<Record<string, unknown>> }) =>
  writeEntity<CatalogItem>(id ? `api/catalog/items/${id}` : 'api/catalog/items', id ? 'PUT' : 'POST', body, 'Failed to save the item');

/** Deletes an item. The server removes its vendor offers with it (the vendors themselves are untouched). */
export const deleteItem = (id: string) => writeEntity<null>(`api/entities/catalog_item/${id}`, 'DELETE', {}, 'Failed to delete the item');

export const saveOffer = (id: string | null, body: Record<string, unknown>) =>
  id ? writeEntity<CatalogOffer>(`api/entities/catalog_item_offer/${id}`, 'PUT', body, 'Failed to save the offer') : writeEntity<CatalogOffer>('api/entities/catalog_item_offer', 'POST', body, 'Failed to save the offer');

/** Where a cost code is used (items, purchase orders, bills, budget lines), with a few examples of each. */
export interface CostCodeUsage {
  cost_code: { id: string; code: string; name: string | null };
  total: number;
  catalog_items: { count: number; examples: string[] };
  purchase_orders: { count: number; examples: string[] };
  bills: { count: number; examples: string[] };
  budget_lines: { count: number; examples: string[] };
}

export const fetchCostCodeUsage = (id: string) => getJson<CostCodeUsage>(`api/cost-codes/${id}/usage`, 'Failed to check where the cost code is used');

/** Moves every reference of a cost code to another one. Nothing is deleted. */
export async function reassignCostCodeReferences(fromId: string, toId: string) {
  const response = await fetch(getApiUrl(`api/cost-codes/${fromId}/reassign`), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ to: toId }) });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || body?.message || 'Failed to move the references');
  return body.data as { moved: Record<string, number>; to: { id: string; code: string } };
}

/** Deletes a cost code. The server refuses (409, saying what uses it) while anything references it. */
export const deleteCostCode = (id: string) => writeEntity<null>(`api/entities/cost_code/${id}`, 'DELETE', {}, 'Failed to delete the cost code');

/** One active vendor offer of an item, as the catalog search returns it (just what a price needs). */
export interface CatalogSearchOffer {
  id: string;
  vendor_id: string;
  unit_price: number;
  is_preferred: boolean;
  is_active: boolean;
}

/** An active catalog item with its cost code and its active vendor offers. */
export interface CatalogSearchRow {
  id: string;
  name: string;
  sku: string | null;
  description: string | null;
  unit: string | null;
  cost_code: { id: string; code: string; name: string | null } | null;
  offers: CatalogSearchOffer[];
}

/**
 * The catalog as a document's pickers see it (GET /api/catalog/search): items found by name / SKU / description / cost code, optionally only
 * those a vendor offers, one page at a time, each with its cost code and active offers. Read-only.
 */
export async function searchCatalog(params: { search?: string; costCodeId?: string; vendorId?: string; limit?: number; offset?: number } = {}): Promise<{ rows: CatalogSearchRow[]; total: number; hasMore: boolean }> {
  const query = new URLSearchParams();
  if (params.search?.trim()) query.set('search', params.search.trim());
  if (params.costCodeId) query.set('cost_code_id', params.costCodeId);
  if (params.vendorId) query.set('vendor_id', params.vendorId);
  if (params.limit) query.set('limit', String(params.limit));
  if (params.offset) query.set('offset', String(params.offset));
  const result = await getJson<{ rows: CatalogSearchRow[]; total: number; has_more: boolean }>(`api/catalog/search${query.toString() ? `?${query}` : ''}`, 'Failed to search the catalog');
  return { rows: result.rows, total: result.total, hasMore: result.has_more };
}
